import { computed, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type { MenuItem } from '@monolith/legacy/components/account/composables/use-account-menu';
import { isDismissed, dismiss } from '@monolith/legacy/components/account/composables/use-promote-shop-highlight';
import Analytics from '@monolith/legacy/services/analytics';
import {
  PromoteShopHighlightViewedEvent,
  PromoteShopHighlightClosedEvent,
} from '@monolith/legacy/services/analytics/events/promote-shop/promote-shop-highlight-events';

export const useAccountMenuHighlight = (
  items: Ref<MenuItem[]> | ComputedRef<MenuItem[]>,
  getCurrentUrl: ComputedRef<string>
) => {
  let highlightTracked = false;
  const dismissedKeys = ref<Set<string>>(new Set());

  const isAccountRoute = computed(() => {
    const path = getCurrentUrl.value;
    return path.startsWith('/account') || path.startsWith('/v2/account');
  });

  const itemsWithHighlight = computed(() => {
    return items.value.map((item: MenuItem) => {
      if (!item.children?.length) {
        return item;
      }

      return {
        ...item,
        children: item.children.map((child) => {
          if (!child.highlight?.dismissKey) {
            return child;
          }
          if (
            dismissedKeys.value.has(child.highlight.dismissKey) ||
            isDismissed(child.highlight.dismissKey, child.highlight.releaseDate)
          ) {
            return { ...child, highlight: undefined };
          }
          return child;
        }),
      };
    });
  });

  const hasHighlight = computed(() => {
    return itemsWithHighlight.value.some((item) => item.children?.some((child) => child.highlight));
  });

  watch(
    hasHighlight,
    (visible) => {
      if (visible && !highlightTracked) {
        highlightTracked = true;
        Analytics.track(new PromoteShopHighlightViewedEvent());
      }
    },
    { immediate: true }
  );

  const handleHighlightClosed = (dismissKey: string) => {
    if (dismissKey) {
      Analytics.track(new PromoteShopHighlightClosedEvent());
      dismiss(dismissKey);
      dismissedKeys.value.add(dismissKey);
    }
  };

  return {
    dismissedKeys,
    isAccountRoute,
    itemsWithHighlight,
    hasHighlight,
    handleHighlightClosed,
  };
};
